import type { QueryClient } from "@tanstack/react-query";
import { catalogueSnapshotQuery, offlineLeaseQuery } from "@/lib/queries";
import { browserStorage } from "@/lib/storage";

/** The outlet the register last opened on, on this device (not a secret: an ID the server checks). */
const KEY = "kopi-register-outlet";

/** Remembers the outlet the register opened on, so the next visit can ask for its menu at once. */
export function rememberRegisterOutlet(outletId: string) {
  try {
    browserStorage()?.setItem(KEY, outletId);
  } catch {
    // Blocked storage: the next visit just asks a moment later.
  }
}

/** The menu and offline lease of an outlet, as the register first asks for them. */
export function prefetchOutletRegister(
  queryClient: QueryClient,
  outletId: string,
  options = { retry: true },
) {
  void queryClient.prefetchQuery({ ...catalogueSnapshotQuery(outletId), retry: options.retry });
  void queryClient.prefetchQuery(offlineLeaseQuery(outletId));
}

/**
 * Asks for the remembered outlet's menu while the session is still being checked, so a repeat visit
 * waits for one round trip instead of two (PRD §5; scripts/perf-budgets.ts). A guess: the server still
 * decides what the caller may read, a stale guess costs one refused request (not retried), and the page
 * asks for the outlet it actually shows.
 */
export function prefetchRememberedRegister(queryClient: QueryClient) {
  let outletId: string | null | undefined;
  try {
    outletId = browserStorage()?.getItem(KEY);
  } catch {
    outletId = null;
  }
  if (outletId) prefetchOutletRegister(queryClient, outletId, { retry: false });
}
